import { useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";
import DefaultApiService from "../../../infrastructure/api/default";
import RequestAssignmentApi from "../../../infrastructure/api/requestAssignment";
import {
  ASSIGNMENT_FILTER_OPTIONS_MANAGEMENT,
  ASSIGNMENT_FILTER_OPTIONS_OPERATOR,
  enrichAssignmentRows,
  inferAssignmentFeatureFromRows,
  persistUserIdentityToStorage,
  setAssignmentUserContext,
} from "./requestAssignmentDisplay";

const parseListResponse = (res) => {
  const raw = res?.data ?? res;
  return Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
};

export const useAssignmentFilteredList = (
  apiPath,
  isManagement = false,
  options = {}
) => {
  const routeLoaderData = useLoaderData();
  const rootLoaderData = useLoaderData("root");
  const prefetchedRows = Array.isArray(routeLoaderData)
    ? routeLoaderData
    : Array.isArray(rootLoaderData)
      ? rootLoaderData
      : [];
  const source = isManagement ? "Management" : "SolarentOperator";
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [featureChecked, setFeatureChecked] = useState(false);
  const [canClaimRequests, setCanClaimRequests] = useState(false);
  const [currentUserRoleName, setCurrentUserRoleName] = useState(null);
  const defaultFilter =
    options.defaultAssignmentFilter ??
    (isManagement ? "all" : "mine,unassigned");
  const extraQueryParams = options.extraQueryParams;
  const [assignmentFilter, setAssignmentFilter] = useState(defaultFilter);
  const [rows, setRows] = useState(() => enrichAssignmentRows(prefetchedRows));
  const [loading, setLoading] = useState(prefetchedRows.length === 0);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    RequestAssignmentApi.getStatus(source).then((status) => {
      if (!cancelled) {
        setFeatureEnabled(status.enabled);
        setCanClaimRequests(status.canClaimRequests);
        setCurrentUserRoleName(status.currentUserRoleName);
        setAssignmentUserContext({
          canClaimRequests: status.canClaimRequests,
          currentUserKind: status.currentUserKind,
          currentUserId: status.currentUserId,
        });
        persistUserIdentityToStorage({
          userKind: status.currentUserKind,
          userId: status.currentUserId,
        });
        setFeatureChecked(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [source]);

  useEffect(() => {
    let cancelled = false;
    const hasPrefetchedList =
      reloadToken === 0 &&
      Array.isArray(routeLoaderData) &&
      routeLoaderData.length > 0;

    const load = async () => {
      if (!hasPrefetchedList) {
        setLoading(true);
      }
      try {
        const params = {
          ...(assignmentFilter ? { assignmentFilter } : {}),
          ...(extraQueryParams || {}),
        };
        const hasParams = Object.keys(params).length > 0;
        const res = await DefaultApiService.getListApi(
          apiPath,
          hasParams ? params : undefined
        );
        if (!cancelled) {
          const nextRows = enrichAssignmentRows(parseListResponse(res));
          setRows(nextRows);
          if (inferAssignmentFeatureFromRows(nextRows)) {
            setFeatureEnabled(true);
          }
        }
      } catch {
        if (!cancelled && prefetchedRows.length > 0) {
          setRows(enrichAssignmentRows(prefetchedRows));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiPath, assignmentFilter, extraQueryParams, reloadToken, routeLoaderData, prefetchedRows]);

  return {
    rows,
    assignmentFilter,
    setAssignmentFilter,
    loading,
    reloadList: () => setReloadToken((t) => t + 1),
    featureEnabled,
    featureChecked,
    canClaimRequests,
    currentUserRoleName,
    showAssignmentUi: featureEnabled,
    filterOptions: isManagement
      ? ASSIGNMENT_FILTER_OPTIONS_MANAGEMENT
      : ASSIGNMENT_FILTER_OPTIONS_OPERATOR,
  };
};
